import { nid } from '#lib/util/nanoid';

/** Tool call from the LLM during agentic loop, with optional result info */
export interface ToolCall {
	name: string;
	arguments: Record<string, unknown>;
	call_id: string;
	description?: string;
	result_count?: number;
	status: 'running' | 'completed' | 'error';
	/** Error message when status === 'error', for debugging only */
	error?: string;
}

/** Topic extracted from the conversation */
export interface TopicInfo {
	topic_description: string;
}

/** An asset (e.g. a generated image) referenced by a specific message; may originate from the user or the agent. */
export interface AssetRef {
	/** Tool call this asset is generated by, used to match the placeholder to its result */
	call_id?: string;
	assetId?: string;
	url?: string;
	name?: string;
	prompt?: string;
	description?: string;
	kind: 'image' | 'file';
	source: 'user' | 'agent';
	status: 'generating' | 'ready' | 'error';
	/** A user-attached document's page count, shown next to its name */
	pageCount?: number;
	/** Error message when status === 'error', for debugging only */
	error?: string;
}

/** Query classification from the classifier */
export interface ClassificationInfo {
	name: string;
	enable_thinking: boolean;
	enable_tools: boolean;
	temperature: number;
	max_iterations: number;
	thinking_budget: number;
}

export class ChatMessage {
	type?: 'message';
	status?:
		| 'pending'
		| 'rag.started'
		| 'rag.tool_call'
		| 'rag.tool_result'
		| 'reasoning'
		| 'in_progress'
		| 'completed'
		| 'incomplete';
	annotations?: any[];
	refusal?: boolean;

	id? = nid();

	/** Topic of the conversation (extracted post-hoc) */
	topic?: TopicInfo;

	/** Query classification result */
	classification?: ClassificationInfo;

	/** Current tool being called (index into toolCalls array) */
	currentToolCallIndex?: number;

	/** Tool calls with their results */
	toolCalls?: ToolCall[];

	timings?: {
		total_seconds: number;
		steps?: Record<string, number>;
	};

	/** Tools available when this message was sent (e.g. ['web_search']) */
	available_tools?: string[];

	/** Assets (e.g. generated images) produced during this message */
	assets?: AssetRef[];

	created?: Date = new Date();

	constructor(
		public role: 'user' | 'assistant' | 'system',
		public content: string,
		public reasoning_content: string = ''
	) {}
}

export type ChatMessages = ChatMessage[];
